import * as React from "react";
import { BackgroundTileCrop } from "@/components/Backgrounds/BackgroundTileCrop";
import { useBackgroundLibrary } from "@/components/Backgrounds/useBackgroundLibrary";
import { Button, Dialog, Label, RadioGroup, Textarea } from "@/components/ui";
import { BackgroundAsset, BackgroundRatio } from "@/services/bedrock/dtos/BackgroundAsset";
import { ContextType } from "@/services/bedrock/dtos/DesignPackage";
import { useDebounced } from "@/utils/useDebounced";

interface GenerateBackgroundsDialogProps {
  open: boolean;
  onOpenChange(open: boolean): void;
  itemPosition: number;
  /** The slide's slot ratio: matches are cropped to it and new images are made in it. */
  ratio: BackgroundRatio;
  contextType: ContextType;
  /** Already on the slide; shown checked and not selectable. */
  excludeAssetIds: number[];
  /** Adds a library match to the slide without spending generation quota. */
  onAttach(asset: BackgroundAsset): Promise<void>;
  onGenerate(input: { hint: string; count: 1 | 2 | 3 }): Promise<void>;
  busy: boolean;
}

const COUNTS = [1, 2, 3] as const;
const MATCH_DEBOUNCE_MS = 400;
const MIN_MATCH_LENGTH = 3;
const MAX_MATCHES = 8;

export const GenerateBackgroundsDialog: React.FC<GenerateBackgroundsDialogProps> = ({
  open,
  onOpenChange,
  itemPosition,
  ratio,
  contextType,
  excludeAssetIds,
  onAttach,
  onGenerate,
  busy,
}) => {
  const [hint, setHint] = React.useState("");
  const [count, setCount] = React.useState<1 | 2 | 3>(2);
  const [attachingId, setAttachingId] = React.useState<number | null>(null);
  const query = useDebounced(hint.trim(), MATCH_DEBOUNCE_MS);
  const searching = open && query.length >= MIN_MATCH_LENGTH;

  React.useEffect(() => {
    if (open) {
      setHint("");
      setCount(2);
    }
  }, [open]);

  const { assets, matches, isFetching } = useBackgroundLibrary(
    { q: query, ratio, suitableFor: contextType, perPage: MAX_MATCHES },
    { enabled: searching }
  );
  const strip = searching ? assets.slice(0, MAX_MATCHES) : [];
  const excluded = new Set(excludeAssetIds);

  const attach = async (asset: BackgroundAsset) => {
    if (attachingId != null) return;
    setAttachingId(asset.id);
    try {
      await onAttach(asset);
      onOpenChange(false);
    } catch {
      // The mutation hook already toasts the error.
    } finally {
      setAttachingId(null);
    }
  };

  const submit = async (event: React.FormEvent) => {
    event.preventDefault();
    try {
      await onGenerate({ hint: hint.trim(), count });
      onOpenChange(false);
    } catch {
      // The mutation hook already toasts the error; keep the dialog open to try again.
    }
  };

  return (
    <Dialog open={open} onOpenChange={next => onOpenChange(next)} size="lg">
      <Dialog.Header>
        <Dialog.Title>Generate more with AI</Dialog.Title>
        <Dialog.Description>Slide {itemPosition}</Dialog.Description>
      </Dialog.Header>
      <form id="generate-backgrounds" onSubmit={submit}>
        <Dialog.Content className="flex flex-col gap-4">
          <Textarea
            aria-label="Hint"
            value={hint}
            onChange={event => setHint(event.target.value)}
            placeholder="e.g. snowy cabin exterior at dusk"
            rows={3}
            disabled={busy}
          />

          {strip.length > 0 && (
            <div className="flex flex-col gap-2" data-testid="background-matches">
              <span className="text-sm font-semibold text-slate-700">
                {matches ?? strip.length} in your library already
              </span>
              <div className="grid grid-cols-4 gap-2 sm:grid-cols-8">
                {strip.map(asset => (
                  <BackgroundTileCrop
                    key={asset.id}
                    asset={asset}
                    ratio={ratio}
                    disabled={excluded.has(asset.id)}
                    busy={attachingId === asset.id}
                    onClick={() => void attach(asset)}
                  />
                ))}
              </div>
              <p className="text-xs text-slate-500">Pick one to add it now, or create new ones below.</p>
            </div>
          )}
          {searching && !isFetching && strip.length === 0 && (
            <p className="text-xs text-slate-500">Nothing similar yet. Create new ones below.</p>
          )}

          <div className="flex flex-col gap-2">
            <span className="text-sm font-semibold text-slate-700" id="generate-backgrounds-count">
              How many?
            </span>
            <RadioGroup
              value={String(count)}
              onValueChange={value => setCount(Number(value) as 1 | 2 | 3)}
              aria-labelledby="generate-backgrounds-count"
              className="flex gap-4"
              disabled={busy}
            >
              {COUNTS.map(n => (
                <Label key={n} className="flex items-center gap-2 text-sm cursor-pointer">
                  <RadioGroup.Item value={String(n)} aria-label={String(n)} />
                  {n}
                </Label>
              ))}
            </RadioGroup>
          </div>
        </Dialog.Content>
      </form>
      <Dialog.Footer>
        <Button type="submit" form="generate-backgrounds" loading={busy}>
          Generate
        </Button>
      </Dialog.Footer>
    </Dialog>
  );
};
